<template>
  <div class="waxt">

    <!-- busy -->
    <div v-if="loading" class="text-center text-primary my-3">
      <b-spinner class="align-middle mx-1"></b-spinner>
      <strong>{{ msg('Loading') }}...</strong>
    </div>

    <template v-else>
      <!-- toolbar: sort direction + page size -->
      <div class="waxt-bar" v-if="messages.length > 0">
        <button type="button" class="waxt-sort" v-b-tooltip.hover :title="msg('Date')" @click="sort.desc = !sort.desc">
          <i :class="sort.desc ? 'fas fa-arrow-down-wide-short' : 'fas fa-arrow-up-short-wide'" class="mr-25"/>
          {{ msg('Date') }}
        </button>
        <span class="waxt-bar__line"/>
        <label class="waxt-label mb-0 mr-50">{{ msg('Show') }}</label>
        <v-select v-model="page.amount" :dir="isRTL ? 'rtl' : 'ltr'" :options="[10, 25, 50, 100]"
                  :clearable="false" class="waxt-page-select" @option:selected="page.currentPage = 1"/>
      </div>

      <!-- conversation inbox -->
      <div v-if="pagedMessages.length > 0" class="waxt-list">
        <div v-for="(message, index) in pagedMessages" :key="message.contractId + '-' + index"
             class="waxt-row waxt-row--unseen">

          <!-- avatar -->
          <span class="waxt-avatar" :style="avatarStyle(message.customerName)"
                role="button" @click="onClientSelect(clients[message.clientId])">
            {{ initials(message.customerName) }}
          </span>

          <!-- identity + preview -->
          <div class="waxt-main">
            <div class="waxt-top">
              <span class="waxt-dot" v-b-tooltip.hover :title="msg('Unseen')"/>
              <button type="button" class="waxt-name" @click="onClientSelect(clients[message.clientId])">
                {{ message.customerName }}
              </button>
              <span v-if="clients[message.clientId] != null" class="waxt-phone tabular">{{ clients[message.clientId].phone }}</span>
              <span class="waxt-source" :class="{'waxt-source--wa': message.source === 'Whatsapp'}">
                <i v-if="message.source === 'Whatsapp'" class="fab fa-whatsapp mr-25"/>{{ message.source }}
              </span>
            </div>
            <div v-if="message.propertyAddress" class="waxt-address">
              <i class="fas fa-home mr-25"/>{{ message.propertyAddress }}
            </div>
            <p v-if="message.lastMessage != null && message.lastMessage !== ''" class="waxt-preview">
              {{ message.lastMessage }}
            </p>
            <p v-else class="waxt-preview waxt-preview--none">{{ msg('noPreview') }}</p>
          </div>

          <!-- time + actions -->
          <div class="waxt-side">
            <span class="waxt-ago" v-b-tooltip.hover :title="dateTitle(message.lastMessageDate)">
              {{ timeAgo(message.lastMessageDate) }}
            </span>
            <span class="waxt-date tabular">{{ dateLabel(message.lastMessageDate) }}</span>
            <div class="waxt-actions">
              <button type="button" class="waxt-action" v-b-tooltip.hover :title="msg('Details')"
                      @click="openSideBar(message)">
                <i class="far fa-eye"/>
              </button>
              <button type="button" class="waxt-action" v-b-tooltip.hover :title="msg('View Contract')"
                      @click="goToContract(message)">
                <i class="fa-solid fa-file-contract"/>
              </button>
              <button type="button" class="waxt-action waxt-action--seen" v-b-tooltip.hover :title="msg('markAsSeen')"
                      @click="markSeen(message)">
                <i class="far fa-check-circle"/>
              </button>
            </div>
          </div>

        </div>
      </div>

      <!-- empty state -->
      <div v-else class="waxt-empty">
        <i class="fab fa-whatsapp"/>
        <p>{{ msg('nodatafound') }}</p>
      </div>
    </template>

    <!-- footer: total + pagination -->
    <div class="waxt-foot">
      <span class="waxt-total"><strong>{{ msg('Total') }}</strong> {{ messages.length }}</span>
      <b-pagination
          v-model="page.currentPage"
          :total-rows="messages.length"
          :per-page="page.amount"
          first-number
          last-number
          class="mb-0"
          @input="refresh"
      />
    </div>

    <ContractRentSidebar ref="contractRentSidebar"/>

  </div>

</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import ContractRentSidebar from "@/components/sidebar/ContractRent/ContractRentSidebar.vue";

const AVATAR_GRADIENTS = [
  'linear-gradient(135deg, #34d399, #059669)',
  'linear-gradient(135deg, #818cf8, #4f46e5)',
  'linear-gradient(135deg, #f472b6, #db2777)',
  'linear-gradient(135deg, #fbbf24, #d97706)',
  'linear-gradient(135deg, #38bdf8, #0284c7)',
  'linear-gradient(135deg, #94a3b8, #64748b)',
];

export default {
  name: 'whatsappTable',
  components: {
    ContractRentSidebar
  },
  data() {
    return {
      messages: [],
      loading: true,
      dialogs: {
        client: false,
      },
      clients: {},
      selected: null,
      sort: {by: 'lastMessageDate', desc: true},
      page: {amount: 10, currentPage: 1, total: 0},

      //Listeners
      listeners: {
        refresh: () => {
        }
      }
    }
  },
  props: {},
  watch: {},
  created() {
    let $this = this
    this.refresh()
    this.$root.$on('crm::messages-table::refresh', $this.listeners.refresh = () => {
      $this.refresh()
    })
  },
  beforeDestroy() {
    this.$root.$off('crm::messages-table::refresh', this.listeners.refresh)
  },
  computed: {
    ...mapGetters('data', ['getBranchName']),
    /** server order is newest-first; the toggle only reverses the view */
    sortedMessages() {
      if (this.sort.desc) {
        return this.messages;
      }
      return this.messages.slice().reverse();
    },
    pagedMessages() {
      const startIndex = (this.page.currentPage - 1) * this.page.amount;
      const endIndex = startIndex + this.page.amount;
      return this.sortedMessages.slice(startIndex, endIndex);
    },
  },
  methods: {
    ...mapActions('messages', ['getAllMessagesWhatsappOnly', 'setMessagesSeen']),
    ...mapActions('clients', ['getClients']),

    onClientSelect(client) {
      if (client == null) {
        return
      }
      this.selected = client.id
      this.dialogs.client = true
      this.$root.$emit('bv::hide::popover')
      this.$root.$emit('crm::client::select', client.id)

    },

    goToContract(message) {
      this.setMessagesSeen(message.contractId)
          .then();

      this.$router.push('/real_estate/contracts/rent/' + message.contractId)
    },

    openSideBar(message) {
      this.$refs.contractRentSidebar.showContract(message.contractId)
    },

    /** same store call goToContract fires, without leaving the inbox */
    markSeen(message) {
      let $this = this;
      this.setMessagesSeen(message.contractId)
          .then(() => {
            $this.messages = $this.messages.filter(m => m.contractId !== message.contractId);
            $this.$emit('count', $this.messages.length);
          });
    },

    initials(name) {
      if (!name) return '?'
      return name.trim().split(/\s+/).slice(0, 2).map(part => part[0]).join('').toUpperCase()
    },
    avatarStyle(name) {
      let hash = 0;
      const str = String(name || '');
      for (let i = 0; i < str.length; i++) {
        hash = (hash * 31 + str.charCodeAt(i)) % 997;
      }
      return {background: AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length]};
    },
    timeAgo(value) {
      let momentValue = this.$moment(value, 'YYYY-MM-DD HH:mm:ss');
      if (!momentValue.isValid()) {
        return '';
      }
      if (momentValue.isSame(this.$moment(), 'day')) {
        return momentValue.format('HH:mm');
      }
      return momentValue.fromNow();
    },
    dateLabel(value) {
      let momentValue = this.$moment(value, 'YYYY-MM-DD HH:mm:ss');
      return momentValue.isValid() ? momentValue.format('DD.MM.YY HH:mm') : '';
    },
    dateTitle(value) {
      let momentValue = this.$moment(value, 'YYYY-MM-DD HH:mm:ss');
      return momentValue.isValid() ? momentValue.format('DD.MM.YYYY HH:mm:ss') : '';
    },

    refresh() {
      let $this = this
      this.loading = true;
      this.$emit('busy', true);

      this.getAllMessagesWhatsappOnly()
          .then($this.onMessagesRetrieve)
          .finally(() => {
            $this.$emit('busy', false);
          })
    },
    onMessagesRetrieve(messagesData) {
      this.messages = messagesData.messages
      this.clients = (messagesData.clients || []).toObject("id");
      this.loading = false
      this.$emit('count', this.messages.length);

    },


  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }

/* toolbar */
.waxt-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.waxt-sort {
  display: inline-flex; align-items: center;
  border: 1px solid rgba(15, 23, 42, .1); border-radius: 999px; background: transparent;
  color: #64748b; font-size: .7rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em;
  padding: 5px 12px; cursor: pointer; transition: all .15s;
  &:hover { border-color: rgba(37, 211, 102, .5); color: #1f9d57; }
  &:focus { outline: none; }
}
.waxt-bar__line { flex: 1; }
.waxt-label {
  font-size: .66rem; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8;
}
.waxt-page-select { min-width: 86px; }
.waxt-page-select :deep(.vs__dropdown-toggle) { min-height: 34px; border-color: rgba(15, 23, 42, .1); }

/* inbox list */
.waxt-list { display: flex; flex-direction: column; }
.waxt-row {
  display: flex; align-items: flex-start; gap: 12px;
  padding: 12px 10px; border-radius: 12px;
  border-bottom: 1px solid rgba(15, 23, 42, .05);
  transition: background .15s;
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(37, 211, 102, .05); }
}
.waxt-row--unseen {
  .waxt-name { font-weight: 800; }
  .waxt-preview { font-weight: 600; }
}

/* avatar */
.waxt-avatar {
  width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  font-size: .78rem; font-weight: 800; color: #fff; user-select: none;
}

/* identity + preview */
.waxt-main { flex: 1; min-width: 0; }
.waxt-top { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.waxt-dot {
  width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0;
  background: #25d366; box-shadow: 0 0 0 3px rgba(37, 211, 102, .2);
}
.waxt-name {
  border: none; background: none; padding: 0; cursor: pointer; text-align: inherit;
  color: #1f2a44; font-size: .88rem; text-transform: capitalize;
  &:hover { color: #1f9d57; text-decoration: underline; }
  &:focus { outline: none; }
}
.waxt-phone { font-size: .72rem; color: #94a3b8; white-space: nowrap; }
.waxt-source {
  display: inline-flex; align-items: center;
  font-size: .62rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em;
  padding: 3px 9px; border-radius: 999px;
  background: rgba(100, 116, 139, .12); color: #64748b;
  &--wa { background: rgba(37, 211, 102, .14); color: #1f9d57; }
}
.waxt-address {
  font-size: .7rem; color: #94a3b8; margin-top: 2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 480px;
  i { opacity: .7; }
}
.waxt-preview {
  margin: 4px 0 0; color: #475569; font-size: .8rem; line-height: 1.35;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  &--none { color: #cbd5e1; font-style: italic; font-weight: 400 !important; }
}

/* right side */
.waxt-side { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
.waxt-ago { font-size: .74rem; font-weight: 800; color: #1f9d57; white-space: nowrap; cursor: default; }
.waxt-date { font-size: .66rem; color: #94a3b8; white-space: nowrap; }
.waxt-actions {
  display: inline-flex; gap: 6px; margin-top: 4px;
  opacity: 0; transition: opacity .15s;
}
.waxt-row:hover .waxt-actions, .waxt-actions:focus-within { opacity: 1; }
@media (hover: none) { .waxt-actions { opacity: 1; } }
.waxt-action {
  width: 28px; height: 28px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  background: #fff; color: #64748b; font-size: .72rem;
  transition: all .15s;
  &:hover { border-color: #7367f0; color: #7367f0; background: rgba(115, 103, 240, .08); }
  &:focus { outline: none; }
  &--seen:hover { border-color: #28c76f; color: #1f9d57; background: rgba(40, 199, 111, .1); }
}

/* empty + footer */
.waxt-empty {
  text-align: center; padding: 40px 0 26px; color: #94a3b8;
  i { font-size: 2.2rem; opacity: .4; }
  p { margin-top: 8px; font-size: .85rem; }
}
.waxt-foot {
  display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 10px;
  padding-top: 12px; margin-top: 6px; border-top: 1px solid rgba(15, 23, 42, .06);
}
.waxt-total { font-size: .8rem; color: #64748b; }
</style>

<style lang="scss">
.dark-layout {
  .waxt-sort { border-color: rgba(255, 255, 255, .16); color: #94a3b8; }
  .waxt-label { color: #8a92a6; }
  .waxt-row { border-color: rgba(255, 255, 255, .06); }
  .waxt-row:hover { background: rgba(37, 211, 102, .08); }
  .waxt-name { color: #e9ecf2; }
  .waxt-preview { color: #b4b7bd; }
  .waxt-preview--none { color: #4b5261; }
  .waxt-action { background: #283046; border-color: rgba(255, 255, 255, .12); color: #94a3b8; }
  .waxt-foot { border-color: rgba(255, 255, 255, .08); }
}
</style>
